/*
 * Heritage Reserve — pending-reserve request store.
 *
 * Companion to heritage-vault.js. While the Vault is "I want to
 * remember this piece," a Reserve is a stronger commitment: "if and
 * when this piece comes back to MDJ unsold, contact me." Reserves
 * are only initiated against out-for-viewing pieces (active product,
 * inventory 0 — currently out on memo with another partner).
 *
 * Local-first: the partner's reserve list lives in localStorage on
 * the device. The same payload is mirrored to MDJ via the
 * integrations webhook (when a reserve endpoint is configured) so the
 * desk gets an email with full contact + product details. State does
 * not sync across devices today — a future iteration could persist
 * to a backend keyed on customer.id.
 *
 * Storage shape:
 *   localStorage["mdj.reserves.items"] = [
 *     {
 *       id: <product.id>,
 *       handle: <product.handle>,
 *       title: string,
 *       image: string,           // CDN url, ~600w
 *       era: string,             // eyebrow line
 *       sku: string,
 *       url: string,             // absolute /products/<handle>
 *       requestedAt: number,     // unix ms
 *       status: 'pending'        // future: 'unavailable' once piece is
 *                                //         confirmed sold by MDJ
 *     },
 *     ...
 *   ]
 *
 * Public API on window.MDJReserves:
 *   getAll(): item[]
 *   has(id): boolean
 *   add(item, context): void   — adds + fires webhook (event: reserve.add)
 *                                  with the full customer-context payload
 *                                  used to compose the MDJ desk email.
 *   remove(id, context): void  — removes + fires webhook (reserve.cancel)
 *   markUnavailable(id): void  — moves an item from pending → unavailable
 *                                  (called by future product-status checks)
 *   subscribe(fn): unsub
 *
 * Custom events (dispatched on document):
 *   mdj:reserves-changed { detail: { items, action, item } }
 */

(function () {
  const KEY = 'mdj.reserves.items';
  const subscribers = new Set();

  function safeStorage() {
    try {
      const k = '__mdj_reserve_test__';
      window.localStorage.setItem(k, k);
      window.localStorage.removeItem(k);
      return window.localStorage;
    } catch (e) {
      return null;
    }
  }

  function read() {
    const s = safeStorage();
    if (!s) return [];
    try {
      const raw = s.getItem(KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function write(items) {
    const s = safeStorage();
    if (!s) return;
    try {
      s.setItem(KEY, JSON.stringify(items));
    } catch (e) {
      if (window.console) console.warn('MDJReserves: localStorage write failed', e);
    }
  }

  function notify(action, item, items) {
    const detail = { action: action, item: item, items: items };
    document.dispatchEvent(new CustomEvent('mdj:reserves-changed', { detail: detail }));
    subscribers.forEach((fn) => {
      try { fn(detail); } catch (e) { /* user fn shouldn't break the store */ }
    });
  }

  /*
   * Webhook payload carries everything the MDJ desk needs to compose
   * an actionable email: the piece (with direct PDP link) and the
   * full customer context (name, email, phone, company, shipping
   * address). The integrations layer stamps shop / currency /
   * timestamp / device_id on top.
   */
  function fireWebhook(action, item, items, context) {
    if (!window.MDJIntegrations || !window.MDJIntegrations.send) return;
    window.MDJIntegrations.send('reserve', {
      event: 'reserve.' + action,
      product: item,
      contact: context || {},
      reserve_size_after: items.length
    });
  }

  function getAll() {
    return read();
  }

  function has(id) {
    if (id == null) return false;
    return read().some((it) => String(it.id) === String(id));
  }

  function add(item, context) {
    if (!item || item.id == null) return;
    const items = read();
    if (items.some((it) => String(it.id) === String(item.id))) return;
    const stamped = Object.assign({}, item, {
      requestedAt: Date.now(),
      status: 'pending'
    });
    items.unshift(stamped);
    write(items);
    notify('add', stamped, items);
    fireWebhook('add', stamped, items, context);
  }

  function remove(id, context) {
    if (id == null) return;
    const items = read();
    const idx = items.findIndex((it) => String(it.id) === String(id));
    if (idx < 0) return;
    const [removed] = items.splice(idx, 1);
    write(items);
    notify('remove', removed, items);
    fireWebhook('cancel', removed, items, context);
  }

  function markUnavailable(id) {
    if (id == null) return;
    const items = read();
    const idx = items.findIndex((it) => String(it.id) === String(id));
    if (idx < 0) return;
    items[idx] = Object.assign({}, items[idx], { status: 'unavailable' });
    write(items);
    notify('mark-unavailable', items[idx], items);
  }

  function subscribe(fn) {
    if (typeof fn !== 'function') return () => {};
    subscribers.add(fn);
    return () => subscribers.delete(fn);
  }

  window.MDJReserves = { getAll, has, add, remove, markUnavailable, subscribe };
})();
